import "./tailwind.css";
import "./global.css";
import "swiper/css";

import { withSentry } from "@sentry/remix";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { LoaderFunction, MetaFunction } from "react-router";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useParams,
  useRouteLoaderData,
} from "react-router";

import { getAuthFeature, getGtm } from "~/core";
import { getDocuments } from "~/core/deprecated/documents";
import { getPageLayoutService } from "~/core/services/get-page-layout.service";
import { getLayoutTranslations } from "~/core/services/static-translations.service";
import {
  domainsSettings,
  PageInfoProvider,
} from "~/features-deprecated/banner/usePageInfoContext";
import {
  runAffiliateMiddleware,
  runAvailableLanguagesMiddleware,
  runLanguageRedirectMiddleware,
  runLoaderAuthMiddleware,
} from "~/middlewares";
import { UIKitProvider } from "~/shared/ui-kit";
import { createCssVars } from "~/shared/ui-kit/ui-kit-provider/createCssVars";
import { createTheme } from "~/shared/utils/create-theme";
import { createLinkComponent } from "~/shared/utils/createLinkComponent";
import { DirectusProvider } from "~/shared/utils/directus/directus.provider";
import { GtmProvider } from "~/shared/utils/gtm-sdk/provider";
import { createGtmInstance } from "~/shared/utils/gtm-sdk/provider/createGtmInstance";
import { AuthModalsProvider } from "~/widgets/auth-modals";
import { Main } from "~/widgets/main";

export const loader = ((o1) => {
  return runAffiliateMiddleware(o1, async (o2) => {
    return runLanguageRedirectMiddleware(o2, (o3) => {
      return runLoaderAuthMiddleware(o3, async (params) => {
        return runAvailableLanguagesMiddleware(
          params,
          async ({
            request,
            context: {
              featureFlags,
              sdk,
              settings,
              affiliate,
              authorizationToken,
              availableLanguages,
            },
          }) => {
            const url = new URL(request.url);
            const domainSettings = (Object.entries(domainsSettings).find(
              ([key]) => url.host.startsWith(key),
            ) ?? [])[1];

            const [gtm, translations, authFeatures, pageLayout] =
              await Promise.all([
                sdk.request(getGtm()),
                sdk.request(getLayoutTranslations()),
                sdk.request(getAuthFeature()),
                sdk.request(getPageLayoutService()),
              ]);

            return {
              domainSettings,
              documents: getDocuments(),
              pageUrl: url,
              featureFlags: {
                isNavigationEnabled: featureFlags.getItem(
                  "headerNavigationLinks",
                ).enabled,
                isLanguageSelectorEnabled: featureFlags.getItem(
                  "headerLanguageSelector",
                ).enabled,
                isCreateButtonEnabled:
                  featureFlags.getItem("headerCreateButton").enabled,
                isFooterNavigationLinkEnabled: featureFlags.getItem(
                  "footerNavigationLinks",
                ).enabled,
                isMenuConstructorEnabled:
                  featureFlags.getItem("menuConstructor").enabled,
                isFooterMenuConstructorEnabled: featureFlags.getItem(
                  "footerMenuConstructor",
                ).enabled,
                isRegistrationFeatureEnabled: featureFlags.getItem(
                  "registration-feature",
                ).enabled,
              },
              affiliate,
              gtmId: gtm?.container_identifier,
              translations,
              header: { ...pageLayout.header, languages: settings.languages },
              cmsPath: process.env.CMS_PUBLIC_PATH,
              sentryDsn: process.env.SENTRY_DSN,
              fontSize: settings.typography,
              fontFamily: settings.font_family,
              fontWeight: settings.font_weight,
              lineHeight: settings.line_height,
              authFeatures,
              availableLanguages,
              isAuth: !!authorizationToken,
              footer: pageLayout.footer,
            };
          },
        );
      });
    });
  });
}) satisfies LoaderFunction;

export function Layout({ children }: { children: ReactNode }): ReactNode {
  const data = useRouteLoaderData<typeof loader>("root");
  const {
    i18n: { language },
  } = useTranslation();
  const gtmId = data?.gtmId;
  const theme = createTheme(language, {
    fontSize: data?.fontSize ?? undefined,
    lineHeight: data?.lineHeight ?? undefined,
    fontWeight: data?.fontWeight ?? undefined,
    fonFamily: {
      main: data?.fontFamily?.main_name ?? undefined,
      additional: data?.fontFamily?.additional_name ?? undefined,
    },
  });
  return (
    <html lang={language} style={createCssVars(theme)}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="new-version" content="1.7.1" />
        <Meta />

        {data?.fontFamily?.main_name && data?.fontFamily?.main_url && (
          <link rel="stylesheet" href={data?.fontFamily?.main_url} />
        )}
        {data?.fontFamily?.additional_name &&
          data?.fontFamily?.additional_url && (
            <link rel="stylesheet" href={data?.fontFamily?.additional_url} />
          )}
        <Links />

        {gtmId && (
          <script
            suppressHydrationWarning={true}
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');`,
            }}
          />
        )}
      </head>

      <body>
        {/* <!-- Google Tag Manager (noscript) --> */}
        {gtmId && (
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            />
          </noscript>
        )}
        {/* <!-- End Google Tag Manager (noscript) --> */}
        <GtmProvider sdk={createGtmInstance()}>
          <UIKitProvider
            value={{
              components: {
                Link: createLinkComponent(language),
              },
              theme,
            }}
          >
            <DirectusProvider url={data?.cmsPath ?? ""}>
              <AuthModalsProvider>{children}</AuthModalsProvider>
            </DirectusProvider>
          </UIKitProvider>
        </GtmProvider>
        <ScrollRestoration />
        <Scripts />
        <script
          dangerouslySetInnerHTML={{
            __html: `if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register("/sw.js").then(() => console.info("service woeker was registred")).catch(function(error) {
        console.error("Ошибка регистрации Service Worker:", error);
      });
    }`,
          }}
        />
      </body>
    </html>
  );
}

export const meta: MetaFunction = () => {
  return [{ title: "LuckyPoker 休闲扑克玩家的天堂" }];
};

function App(): ReactNode {
  const { lang } = useParams();
  const loaderData = useLoaderData<typeof loader>();
  const { pageUrl, domainSettings, sentryDsn } = loaderData;

  return (
    <>
      <span
        id={"sentryDsn"}
        data-value={sentryDsn}
        style={{ position: "absolute" }}
      />
      <Main lang={lang ?? ""} {...loaderData}>
        <PageInfoProvider url={pageUrl} domainSettings={domainSettings}>
          <Outlet />
        </PageInfoProvider>
      </Main>
    </>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export default withSentry(App);
